import { useEffect, useState } from 'react';
import ActionStatusText from '../../components/ui/ActionStatusText';
import ErrorMessage from '../../components/ui/ErrorMessage';
import { MEAL_TYPE_MAP } from '../../config/meal-config';
import { useModalEntryType, useModalNavigation } from '../../context/ModalControllerContext';
import useSaveOwnMeal from '../../hooks/data/meals/useSaveOwnMeal';
import ModalHeader from '../components/ModalHeader';
import ModalMenu from '../components/ModalMenu';
import { buildOwnMealPayload } from '../../helpers/payloads';

export const DEFAULT_FORM = { name: '', kcal: '', protein: '', carbs: '', fat: '' };

const ModalOwnMeal = ({ currentDay, savedMeals, onAddSavedMeal }) => {
    const { entryType, setEntryType } = useModalEntryType();

    const { goBack, close } = useModalNavigation();

    const [isManualAdd, setIsManualAdd] = useState(true);

    const [mealForm, setMealForm] = useState(DEFAULT_FORM);

    const { saveOwnMeal, isSaving, isSuccess, isError, error } = useSaveOwnMeal({
        currentDay,
        onAddSavedMeal,
        setMealForm,
        setIsManualAdd
    });

    // HANDLERS

    const handleSave = () => {
        const payload = buildOwnMealPayload(mealForm, entryType);
        if (!payload) return;

        saveOwnMeal(payload);
    };

    const handleNumberChange = (key) => (e) => {
        const value = e.target.value;

        if (value === '' || (!isNaN(value) && Number(value) >= 0)) {
            setMealForm((prev) => ({ ...prev, [key]: value }));
        }
    };

    // DERIVED

    const entryKey = MEAL_TYPE_MAP[entryType];
    const isAddingMeal = isManualAdd || savedMeals[entryKey]?.length === 0;
    const isProcessing = isSaving || isSuccess;

    // FORM VALIDATION

    const trimmedName = mealForm.name.trim();
    const parsedKcal = parseFloat(mealForm.kcal);
    const isValidKcal = !Number.isNaN(parsedKcal) && parsedKcal > 0;

    // CLEANUP

    useEffect(() => () => setMealForm(DEFAULT_FORM), []);

    return (
        <>
            <ModalHeader
                title="Lägg till egen måltid"
                onClose={close}
                onGoBack={goBack}
                disableButtons={isSaving}
            />

            <div className="modal-body own-meal">
                <ModalMenu activeMealType={entryType} onSelect={(mealType) => setEntryType(mealType)} />

                {!isError ? (
                    isAddingMeal ? (
                        <div className="own-meal-input-wrapper">
                            <div className="input-with-label">
                                <span className="inline-label">Namn:</span>
                                <input
                                    type="text"
                                    id="meal-name-input"
                                    className="modal-input"
                                    value={mealForm.name}
                                    onChange={(e) =>
                                        setMealForm((prev) => ({ ...prev, name: e.target.value }))
                                    }
                                    disabled={isProcessing}
                                />
                            </div>

                            <div className="input-with-label">
                                <span className="inline-label">Kcal:</span>
                                <input
                                    type="number"
                                    id="meal-kcal-input"
                                    className="modal-input"
                                    min="0"
                                    step="any"
                                    value={mealForm.kcal}
                                    onChange={handleNumberChange('kcal')}
                                    disabled={isProcessing}
                                />
                            </div>

                            <div className="input-with-label">
                                <span className="inline-label">Protein:</span>
                                <input
                                    type="number"
                                    id="meal-protein-input"
                                    className="modal-input"
                                    placeholder="Frivilligt"
                                    min="0"
                                    step="any"
                                    value={mealForm.protein}
                                    onChange={handleNumberChange('protein')}
                                    disabled={isProcessing}
                                />
                            </div>

                            <div className="input-with-label">
                                <span className="inline-label">Fett:</span>
                                <input
                                    type="number"
                                    id="meal-fat-input"
                                    className="modal-input"
                                    placeholder="Frivilligt"
                                    min="0"
                                    step="any"
                                    value={mealForm.fat}
                                    onChange={handleNumberChange('fat')}
                                    disabled={isProcessing}
                                />
                            </div>

                            <div className="input-with-label">
                                <span className="inline-label">Kolhydrater:</span>
                                <input
                                    type="number"
                                    id="meal-carbs-input"
                                    className="modal-input"
                                    placeholder="Frivilligt"
                                    min="0"
                                    step="any"
                                    value={mealForm.carbs}
                                    onChange={handleNumberChange('carbs')}
                                    disabled={isProcessing}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="own-meal-wrapper">
                            {(savedMeals[entryKey] ?? []).map((meal, index) => (
                                <div className="own-meal" key={meal.name + meal.kcal + index}>
                                    <span className="own-meal-name">{meal.name}</span>
                                    <span className="own-meal-kcal">{meal.kcal} kcal</span>
                                </div>
                            ))}
                        </div>
                    )
                ) : (
                    <ErrorMessage message={error?.message || 'Ett fel uppstod vid sparandet. Försök igen.'} />
                )}
            </div>

            <div className="modal-footer own-meal">
                <button
                    type="button"
                    className="btn-green bold-title"
                    id="save-own-meal-btn"
                    disabled={isProcessing || !trimmedName || !isValidKcal}
                    onClick={handleSave}
                >
                    <ActionStatusText isProcessing={isSaving} isCompleted={!isAddingMeal && isSuccess} />
                </button>

                <button
                    type="button"
                    className="border-btn-green bold-title"
                    id="add-own-meal-btn"
                    onClick={() => {
                        setMealForm(DEFAULT_FORM);
                        setIsManualAdd(true);
                    }}
                    disabled={isProcessing || isAddingMeal}
                >
                    <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="2" />
                        <line x1="8" y1="4" x2="8" y2="12" stroke="currentColor" strokeWidth="2" />
                        <line x1="4" y1="8" x2="12" y2="8" stroke="currentColor" strokeWidth="2" />
                    </svg>
                    Lägg till en till
                </button>
            </div>
        </>
    );
};

export default ModalOwnMeal;
